import {
  HttpClient,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@perval/shared';
import { catchError, firstValueFrom, map, of, take } from 'rxjs';
import {
  SignUpResponse,
  SignUpResponseHeaders,
} from '../../models/sign-up-response.model';
import { mapSignUpStateToRequest } from '../../mappers/sign-up-request.mapper';
import { SignUpState } from '../../components/sign-up/interfaces/sign-up-store.interface';
import { ErrorStateService } from '../../../rapport/state/error-state.service';

@Injectable({
  providedIn: 'root',
})
export class CustomerService {
  private readonly mainUrl = `${environment.baseApiUrl}/perval-utilisateurs`;
  static readonly DEFAULT_ERROR_MESSAGE =
    "Une erreur est survenue lors de l'inscription.";
  static readonly DEFAULT_ERROR_STATUS = 500;

  constructor(
    private readonly http: HttpClient,
    private readonly errorStateService: ErrorStateService,
  ) {}

  public async addCustomer(
    state: SignUpState,
    captchaToken: string,
  ): Promise<SignUpResponse | null> {
    return firstValueFrom(
      // observe 'response' : on récupère la réponse HTTP complète pour accéder
      // aux headers (Location, Message).
      // responseType 'text' : on désactive le parse JSON automatique du
      // FetchBackend. Sinon, quand le backend renvoie une erreur en texte brut,
      // le JSON.parse échoue et Angular remplace le corps par un SyntaxError
      // (le texte d'origine est perdu). En texte, error.error contient le
      // message backend exact.
      this.http
        .post(`${this.mainUrl}/customers`, mapSignUpStateToRequest(state, captchaToken), {
          observe: 'response',
          responseType: 'text',
        })
        .pipe(
          take(1),
          map((response) => CustomerService.toSignUpResponse(response)),
          catchError((error: HttpErrorResponse) => {
            this.errorStateService.setError(
              CustomerService.extractErrorMessage(error),
              false,
              error?.status ?? CustomerService.DEFAULT_ERROR_STATUS,
            );
            return of(null);
          }),
        ),
    );
  }

  // On extrait uniquement les headers Location et Message : soit les deux sont
  // présents, soit on renvoie un objet headers vide.
  private static toSignUpResponse(
    response: HttpResponse<string>,
  ): SignUpResponse {
    const location = response.headers.get('Location');
    const message = response.headers.get('Message');

    const headers: SignUpResponseHeaders =
      location && message ? { location, message } : {};

    return { headers };
  }

  // error.error est ici la chaîne brute renvoyée par le backend. Elle peut être
  // du JSON structuré ({ errors: [{ message }] } / { message }) ou du texte brut.
  private static extractErrorMessage(error: HttpErrorResponse): string {
    const raw = typeof error?.error === 'string' ? error.error.trim() : '';

    if (!raw) {
      return CustomerService.DEFAULT_ERROR_MESSAGE;
    }

    try {
      const parsed = JSON.parse(raw);
      return parsed?.errors?.[0]?.message ?? parsed?.message ?? raw;
    } catch {
      return raw;
    }
  }
}
